// Admin user management API client

const API_BASE = '/api/vehicle-auth';

function getAuthHeaders() {
  const token = localStorage.getItem('vehicle.token');
  return {
    'Content-Type': 'application/json',
    'Authorization': token ? `Bearer ${token}` : '',
  };
}

/**
 * List users with pagination
 * @param {number} limit - Number of users per page
 * @param {number} offset - Offset for pagination
 * @returns {Promise<{users: Array, total: number, limit: number, offset: number}>}
 */
export async function listUsers(limit = 50, offset = 0) {
  const res = await fetch(`${API_BASE}/admin/users?limit=${limit}&offset=${offset}`, {
    headers: getAuthHeaders(),
  });

  if (res.status === 401 || res.status === 403) {
    throw new Error('Unauthorized - admin access required');
  }

  if (!res.ok) {
    throw new Error(`Failed to list users: ${res.status}`);
  }

  return res.json();
}

/**
 * Get single user by ID
 * @param {string} userId - User UUID
 * @returns {Promise<Object>}
 */
export async function getUser(userId) {
  const res = await fetch(`${API_BASE}/admin/users/${userId}`, {
    headers: getAuthHeaders(),
  });

  if (res.status === 404) {
    throw new Error('User not found');
  }

  if (res.status === 401 || res.status === 403) {
    throw new Error('Unauthorized - admin access required');
  }

  if (!res.ok) {
    throw new Error(`Failed to get user: ${res.status}`);
  }

  return res.json();
}

/**
 * Create new user
 * @param {Object} userData - User creation data
 * @returns {Promise<{id: string, username: string}>}
 */
export async function createUser(userData) {
  const res = await fetch(`${API_BASE}/admin/users`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(userData),
  });

  if (res.status === 409) {
    throw new Error('Username already exists');
  }

  if (res.status === 401 || res.status === 403) {
    throw new Error('Unauthorized - admin access required');
  }

  if (!res.ok) {
    throw new Error(`Failed to create user: ${res.status}`);
  }

  return res.json();
}

/**
 * Update user profile
 * @param {string} userId - User UUID
 * @param {Object} updates - Fields to update
 * @returns {Promise<{success: boolean}>}
 */
export async function updateUser(userId, updates) {
  const res = await fetch(`${API_BASE}/admin/users/${userId}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(updates),
  });

  if (res.status === 404) {
    throw new Error('User not found');
  }

  if (res.status === 401 || res.status === 403) {
    throw new Error('Unauthorized - admin access required');
  }

  if (!res.ok) {
    throw new Error(`Failed to update user: ${res.status}`);
  }

  return res.json();
}

/**
 * Update user roles
 * @param {string} userId - User UUID
 * @param {Array<string>} roles - New roles array
 * @returns {Promise<{success: boolean}>}
 */
export async function updateUserRoles(userId, roles) {
  const res = await fetch(`${API_BASE}/admin/users/${userId}/roles`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify({ roles }),
  });

  if (res.status === 401 || res.status === 403) {
    throw new Error('Unauthorized - admin access required');
  }

  if (!res.ok) {
    throw new Error(`Failed to update roles: ${res.status}`);
  }

  return res.json();
}

/**
 * Reset user password
 * @param {string} userId - User UUID
 * @param {string} newPassword - New password
 * @returns {Promise<{success: boolean}>}
 */
export async function resetUserPassword(userId, newPassword) {
  const res = await fetch(`${API_BASE}/admin/users/${userId}/password`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify({ new_password: newPassword }),
  });

  if (res.status === 404) {
    throw new Error('User not found');
  }

  if (res.status === 401 || res.status === 403) {
    throw new Error('Unauthorized - admin access required');
  }

  if (!res.ok) {
    throw new Error(`Failed to reset password: ${res.status}`);
  }

  return res.json();
}

/**
 * List all available roles
 * @returns {Promise<{roles: Array}>}
 */
export async function listRoles() {
  const res = await fetch(`${API_BASE}/admin/roles`, {
    headers: getAuthHeaders(),
  });

  if (res.status === 401 || res.status === 403) {
    throw new Error('Unauthorized - admin access required');
  }

  if (!res.ok) {
    throw new Error(`Failed to list roles: ${res.status}`);
  }

  return res.json();
}
